/**
 * @license Copyright © HatioLab Inc. All rights reserved.
 */

import { css, html, LitElement, PropertyValues } from 'lit'
import { property, query, queryAll } from 'lit/decorators.js'

import { Scene, SCENE_MODE } from '@hatiolab/things-scene'
import { ScopedElementsMixin } from '@open-wc/scoped-elements'

import { ComponentGroup } from '../../types.js'
import { ComponentMenu } from './component-menu.js'
import { ICON_EDIT_MODE, ICON_SHIFT_MODE } from './mode-icons.js'

export class ComponentToolbar extends ScopedElementsMixin(LitElement) {
  static styles = [
    css`
      :host {
        left: 0;
        display: block;
        position: relative;

        width: var(--component-toolbar-icon-size);
        background-color: var(--component-toolbar-background-color);
      }

      [components] {
        display: block;
        padding: 0;
        margin: 0;

        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      [components] > img {
        display: flex;
        flex-direction: row;

        width: var(--component-toolbar-icon-size);
        height: var(--component-toolbar-icon-size);
        min-width: 50%;

        border-bottom: var(--component-toolbar-border);
        margin: 0;
        padding: 6px;
        box-sizing: border-box;
      }

      #shift[active] {
        background-color: #beb9b3;
      }

      component-menu {
        position: absolute;
        top: 0;
        left: var(--component-toolbar-icon-size);
        height: 100%;
        outline: none;
      }

      component-menu[hidden] {
        display: none;
      }
    `
  ]

  @property({ type: Array }) componentGroupList: ComponentGroup[] = []
  @property({ type: String }) group?: string | null
  @property({ type: Object }) scene!: Scene
  @property({ type: Number }) mode: SCENE_MODE = SCENE_MODE.EDIT

  @query('#shift') shift!: HTMLImageElement
  @query('component-menu') private menu!: HTMLElement
  @query('div[components]') private componentsContainer!: HTMLElement
  @queryAll('[data-group]') private groups!: NodeListOf<HTMLElement>

  private icons: { [name: string]: string } = {}

  updated(changes: PropertyValues<this>) {
    if (changes.has('componentGroupList')) {
      const color = getComputedStyle(this, null).getPropertyValue('--md-sys-color-primary')

      this.icons = (this.componentGroupList || [])
        .filter(group => group.templates?.length > 0)
        .reduce(
          (sum, group: ComponentGroup) => {
            sum[group.name] =
              'data:image/svg+xml;charset=UTF-8;base64,' + btoa(group.icon.replace(/{{strokeColor}}/g, color))
            return sum
          },
          {} as { [name: string]: string }
        )

      this.icons['mode-edit'] =
        'data:image/svg+xml;charset=UTF-8;base64,' + btoa(ICON_EDIT_MODE.replace(/{{strokeColor}}/g, color))
      this.icons['mode-shift'] =
        'data:image/svg+xml;charset=UTF-8;base64,' + btoa(ICON_SHIFT_MODE.replace(/{{strokeColor}}/g, color))
    }

    if (changes.has('group')) {
      this.groups.forEach(group => {
        group.getAttribute('data-group') === this.group
          ? group.setAttribute('active', '')
          : group.removeAttribute('active')
      })

      this.group ? this.menu.removeAttribute('hidden') : this.menu.setAttribute('hidden', '')
    }
  }

  static get scopedElements() {
    return {
      'component-menu': ComponentMenu
    }
  }

  render() {
    const componentList = this.componentGroupList || []
    const modeIcon = this.icons[this.mode === SCENE_MODE.SHIFT ? 'mode-shift' : 'mode-edit']

    return html`
      <div components @mousewheel=${(e: MouseEvent) => this.onWheelEvent(e)}>
        <img id="shift" .src=${modeIcon} @click=${(e: MouseEvent) => this._onClickShift(e)} />

        ${componentList
          .filter(group => group.templates?.length > 0)
          .map(
            group => html`
              <img
                data-group=${group.name}
                @click=${(e: MouseEvent) => this._onClickGroup(e)}
                .src=${this.icons[group.name]}
              />
            `
          )}
      </div>

      <component-menu
        tabindex="-1"
        @focusout=${() => {
          this.group = null
        }}
        .scene=${this.scene}
        .group=${this.group}
        .groups=${componentList}
        hidden
      >
      </component-menu>
    `
  }

  onWheelEvent(e: Event) {
    var delta = Math.max(-1, Math.min(1, (e as WheelEvent).deltaY || -(e as WheelEvent).detail))
    this.componentsContainer.scrollTop += delta * 10

    e.preventDefault()
  }

  _setMode(mode: SCENE_MODE) {
    this.mode = mode

    this.dispatchEvent(
      new CustomEvent('mode-changed', {
        bubbles: true,
        composed: true,
        detail: { value: mode }
      })
    )
  }

  _onClickShift(e: MouseEvent) {
    const shift = this.shift

    if (shift.hasAttribute('active')) {
      shift.removeAttribute('active')
      this._setMode(SCENE_MODE.EDIT)
    } else {
      shift.setAttribute('active', '')
      this._setMode(SCENE_MODE.SHIFT)
    }
  }

  async _onClickGroup(e: MouseEvent) {
    var button = e.target as HTMLElement

    this.group = button!.closest('[data-group]')?.getAttribute('data-group')

    if (!this.group) return

    await this.updateComplete

    this.menu?.focus()
  }
}
